import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { incidentService } from "@/services/incidentService";

import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useNavigation } from "@react-navigation/native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Table, ColumnDef, EditableOption } from "@/components/NewTable";
import { theme } from "@/constants/themes";

// Status color mapping
const STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "attention" | "neutral" | "todo">> = {
  "PENDING REVIEW": "todo",
  "UNDER REVIEW": "pending",
  ESCALATED: "attention",
  RESOLVED: "complete",
};

export function RelatedIncidentsScreen() {
  const { colors } = useTheme();
  const { formatDate } = useDateTime();
  const { displayNames } = useAuth();
  const { incidents: incidentPermissions } = usePermissions();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const navigation = useNavigation();
  const { navigateToEntity } = useNavigationGuard();
  const [incidents, setIncidents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");

  // Set up header with search
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <ThemedText style={styles.headerTitle}>{displayNames?.incidents?.plural || "Incidents"}</ThemedText>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
    });
  }, [navigation, searchValue, colors, displayNames?.incidents?.plural]);

  useEffect(() => {
    const fetchIncidents = async () => {
      if (!currentEntity?.currentId || !currentEntity?.currentPanel) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let fetchedIncidents: any[] = [];

        switch (currentEntity.currentPanel) {
          case "client":
            fetchedIncidents = await incidentService.getIncidentsByClientId(currentEntity.currentId);
            break;
          case "project":
            fetchedIncidents = await incidentService.getIncidentsByProjectId(currentEntity.currentId);
            break;
          case "shift":
            fetchedIncidents = await incidentService.getIncidentsByShiftId(currentEntity.currentId);
            break;
          default:
            console.warn(`No incident relationship defined for entity type: ${currentEntity.currentPanel}`);
        }

        setIncidents(fetchedIncidents);
      } catch (err: any) {
        console.error("Failed to fetch related incidents:", err);
        setError(err.message || "Failed to load incidents");
      } finally {
        setLoading(false);
      }
    };

    fetchIncidents();
  }, [currentEntity?.currentId, currentEntity?.currentPanel]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getStatusColor = (status?: string) => {
    const colorKey = STATUS_COLORS[status?.toUpperCase() || "PENDING REVIEW"] || "todo";
    return colors[colorKey].border;
  };

  // Status options for editable picker
  const statusOptions: EditableOption[] = useMemo(
    () => [
      { value: "PENDING REVIEW", label: "Pending Review", color: colors.todo.border },
      { value: "UNDER REVIEW", label: "Under Review", color: colors.pending.border },
      { value: "ESCALATED", label: "Escalated", color: colors.attention.border },
      { value: "RESOLVED", label: "Resolved", color: colors.complete.border },
    ],
    [colors],
  );

  // Handle edit callback
  const handleEdit = useCallback(async (row: any, key: string, newValue: string) => {
    if (!row.id) return;

    try {
      await incidentService.updateIncident(row.id, { [key]: newValue });
      setIncidents((prev) => prev.map((inc) => (inc.id === row.id ? { ...inc, [key]: newValue } : inc)));
    } catch (err) {
      console.error("Failed to update incident:", err);
    }
  }, []);

  // Handle delete callback
  const handleDelete = useCallback(async (row: any) => {
    if (!row.id) return;

    try {
      await incidentService.deleteIncidentsByIds([row.id]);
      setIncidents((prev) => prev.filter((inc) => inc.id !== row.id));
    } catch (err) {
      console.error("Failed to delete incident:", err);
    }
  }, []);

  const handleIncidentPress = useCallback(
    (incident: any) => {
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }

      navigateToEntity("incident", incident.id!);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  const handleProjectPress = useCallback(
    (incident: any) => {
      if (!incident.project?.id) return;
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      navigateToEntity("project", incident.project.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  // Define columns for the table
  const columns: ColumnDef<any>[] = useMemo(() => {
    const cols: ColumnDef<any>[] = [];

    // Name column - clickable for navigation
    cols.push({
      key: "name",
      header: "Name",
      flex: 1,
      minWidth: 120,
      accessor: (row: any) => row.name || "—",
      onPress: (row) => handleIncidentPress(row),
    });

    // Status column with editable picker
    cols.push({
      key: "status",
      header: "Status",
      width: 160,
      align: "center",
      searchable: true,
      searchValue: (_, row) => row.status || "PENDING REVIEW",
      editable: {
        type: "picker",
        options: statusOptions,
        placeholder: "Select status",
      },
      render: (value, row) => (
        <View style={[styles.statusPill, { backgroundColor: getStatusColor(row.status) }]}>
          <ThemedText style={styles.statusText}>{row.status?.toUpperCase() || "PENDING REVIEW"}</ThemedText>
        </View>
      ),
    });

    // Type column
    cols.push({
      key: "incident_type",
      header: "Type",
      width: 120,
      accessor: (row: any) => row.incident_type || "—",
    });

    // Occurred date column
    cols.push({
      key: "occurred_date",
      header: "Occurred",
      width: 110,
      searchable: false,
      render: (_value, row) => (
        <ThemedText style={styles.cellText}>{row.occurred_date ? formatDate(row.occurred_date) : "—"}</ThemedText>
      ),
    });

    // Submitted by column
    cols.push({
      key: "submitted_by_user",
      header: "Submitted By",
      width: 140,
      accessor: (row: any) => row.submitted_by_user?.name || "—",
    });

    // Project column (hidden when viewing from project panel)
    if (currentEntity?.currentPanel !== "project") {
      cols.push({
        key: "project",
        header: displayNames?.projects?.name || "Project",
        flex: 1,
        minWidth: 120,
        accessor: (row: any) => row.project?.name || "—",
        onPress: (row) => handleProjectPress(row),
      });
    }

    return cols;
  }, [
    statusOptions,
    formatDate,
    getStatusColor,
    handleIncidentPress,
    handleProjectPress,
    currentEntity?.currentPanel,
    displayNames?.projects?.name,
  ]);

  const incidentsName = displayNames?.incidents?.plural?.toLowerCase() || "incidents";

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="alert-circle" size={48} color={colors.red.text} />
          <ThemedText style={styles.emptyText}>Failed to load {incidentsName}</ThemedText>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<any>
        data={incidents}
        columns={columns}
        keyExtractor={(item) => item.id || ""}
        loading={loading}
        loadingRowCount={8}
        onEdit={handleEdit}
        canEdit={() => incidentPermissions.update}
        onDelete={handleDelete}
        canDelete={() => incidentPermissions.delete}
        searchValue={searchValue}
        sortable
        defaultSort={{ key: "occurred_date", direction: "desc" }}
        emptyMessage={`No ${incidentsName} found`}
        emptyIcon="alert-circle-outline"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "600",
    color: "white",
  },
  cellText: {
    fontSize: 12,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    opacity: 0.6,
  },
});
